import type { ReactNode, SVGProps } from "react";

/**
 * The one icon catalogue for navigation and actions.
 *
 * Every drawing sits on a 24×24 grid with a 1.8px nominal stroke (the
 * `--icon-stroke` token), round caps and joins, no fill. Render it at 16, 18
 * or 20px (`--icon-sm` / `--icon-md` / `--icon-lg`); a context stylesheet may
 * size it but never changes the stroke. Same action = same drawing: "Lotes"
 * is always stacked layers, "Calculadora" always the calculator, "Exportar"
 * always the tray arrow.
 *
 * Out of scope on purpose: the SH chapter tiles (36×36 catalogue identity,
 * `ncm-chapter-mark.tsx`) and the fiscal family glyphs (`FamilyGlyph`), which
 * carry fiscal meaning rather than an action.
 */
export type IconName =
  | "overview"
  | "evidence"
  | "sources"
  | "proposals"
  | "rulesets"
  | "rules"
  | "calculator"
  | "calculations"
  | "lots"
  | "invoices"
  | "verification"
  | "audit"
  | "settings"
  | "users"
  | "legal"
  | "agents"
  | "webResearch"
  | "demos"
  | "howItWorks"
  | "agentFindings"
  | "uploadFailures"
  | "sessions"
  | "infraCost"
  | "autoApproval"
  | "key"
  | "logs"
  | "book"
  | "lab"
  | "governance"
  | "automation"
  | "admin"
  | "panel"
  | "operations"
  | "developers"
  | "back"
  | "check"
  | "chevron"
  | "chevronUp"
  | "chevronLeft"
  | "chevronRight"
  | "video"
  | "quiz"
  | "leave"
  | "search"
  | "close"
  | "arrowRight"
  | "sortAsc"
  | "sortDesc"
  | "sortNone"
  | "filter"
  | "columns"
  | "density"
  | "download"
  | "external"
  | "refresh"
  | "undo"
  | "flag"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "lock"
  | "people"
  | "disabled"
  | "unavailable"
  | "calendar";

export type IconSize = 16 | 18 | 20;

const ICONS: Record<IconName, ReactNode> = {
  overview: (
    <>
      <path d="M4.5 10.2 12 4.5l7.5 5.7" />
      <path d="M6.5 8.8v10.7h11V8.8" />
      <path d="M10 19.5v-5h4v5" />
    </>
  ),
  evidence: (
    <>
      <path d="M7 3.5h8.5L19 7v13.5H7z" />
      <path d="M15.5 3.5V7H19" />
      <path d="M9.5 11h5" />
      <path d="M9.5 14.5h4" />
    </>
  ),
  sources: (
    <>
      <circle cx="7" cy="7.5" r="2.35" />
      <circle cx="17" cy="7.5" r="2.35" />
      <circle cx="12" cy="17" r="2.35" />
      <path d="M9.35 7.5h5.3" />
      <path d="M8.7 9.4 10.6 15" />
      <path d="M15.3 9.4 13.4 15" />
    </>
  ),
  proposals: (
    <>
      <path d="M6.5 3.5h7.5L18 7.5v3" />
      <path d="M14 3.5V7h3.5" />
      <path d="M6.5 3.5v17H11" />
      <path d="M12.5 20.5 21 12l-2.4-2.4-8.5 8.5v2.4z" />
      <path d="m16.2 11.2 2.4 2.4" />
    </>
  ),
  rulesets: (
    <>
      <path d="M8 5h11v3.5" />
      <path d="M19 5v3.5" />
      <path d="M5.5 8.5h13v12h-13z" />
    </>
  ),
  rules: (
    <>
      <path d="M9 7h11" />
      <path d="M9 12h11" />
      <path d="M9 17h8" />
      <path d="M5.5 7h.01" />
      <path d="M5.5 12h.01" />
      <path d="M5.5 17h.01" />
    </>
  ),
  calculations: (
    <>
      <path d="M6.5 3.75h11v16.5h-11z" />
      <path d="M8.75 6.25h6.5v3.25h-6.5z" />
      <path d="M8.75 12.25h2.25" />
      <path d="M13 12.25h2.25" />
      <path d="M8.75 16h2.25" />
      <path d="M13 16h2.25" />
    </>
  ),
  /* Lotes: stacked layers, never the calculator glyph. */
  lots: (
    <>
      <path d="m12 4 8 4.25-8 4.25-8-4.25z" />
      <path d="m4 12.25 8 4.25 8-4.25" />
      <path d="m4 16.25 8 4.25 8-4.25" />
    </>
  ),
  /* Notas fiscais: a receipt with its tally lines. */
  invoices: (
    <>
      <path d="M6.5 3.5h11v17l-2.75-1.8-2.75 1.8-2.75-1.8-2.75 1.8z" />
      <path d="M9.5 8h5" />
      <path d="M9.5 11.5h5" />
      <path d="M9.5 15h2.5" />
    </>
  ),
  verification: (
    <>
      <path d="M12 3.5 19 6v5.5c0 4.3-2.9 7.6-7 9-4.1-1.4-7-4.7-7-9V6z" />
      <path d="m8.9 12.1 2.2 2.2 4.1-4.3" />
    </>
  ),
  governance: (
    <>
      <path d="M12 4.5v15" />
      <path d="M8 19.5h8" />
      <path d="M5.5 7.5h13" />
      <path d="m5.5 7.5-2.5 6h5z" />
      <path d="m18.5 7.5-2.5 6h5z" />
    </>
  ),
  automation: (
    <>
      <path d="m11 3.5 1.7 4.3 4.3 1.7-4.3 1.7L11 15.5l-1.7-4.3L5 9.5l4.3-1.7z" />
      <path d="m17.5 14.5.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
    </>
  ),
  admin: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.3M12 18.2v2.3M20.5 12h-2.3M5.8 12H3.5M18 6l-1.6 1.6M7.6 16.4 6 18M18 18l-1.6-1.6M7.6 7.6 6 6" />
    </>
  ),
  panel: (
    <>
      <path d="M4.5 5.5h15v13h-15z" />
      <path d="M9.5 5.5v13" />
    </>
  ),
  calculator: (
    <>
      <path d="M6.5 3.75h11v16.5h-11z" />
      <path d="M8.75 6.25h6.5v3.25h-6.5z" />
      <path d="M9 13h2M13 13h2M9 16.5h2M13 16.5h2" />
    </>
  ),
  audit: (
    <>
      <path d="M5 8.2a7.2 7.2 0 1 0 2.15-5.05" />
      <path d="M4.6 4.6v3.6h3.6" />
      <path d="M12 10.2v3.3l2.6 1.5" />
    </>
  ),
  settings: (
    <>
      <path d="M4.5 7h15" />
      <path d="M4.5 12h15" />
      <path d="M4.5 17h15" />
      <path d="M9 5.1v3.8" />
      <path d="M15 10.1v3.8" />
      <path d="M10 15.1v3.8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="2.4" />
      <path d="M4.6 17.4c.5-3 2.2-4.6 4.4-4.6s3.9 1.6 4.4 4.6" />
      <circle cx="16.2" cy="8.6" r="2.1" />
      <path d="M14.2 17.4c.4-2.4 1.6-3.7 3.4-3.7 1.5 0 2.6.9 3.1 2.5" />
    </>
  ),
  legal: (
    <>
      <path d="M5.5 19.5h13" />
      <path d="M7 19.5V8.5l5-3.2 5 3.2v11" />
      <path d="M10 11.5h1.6" />
      <path d="M12.4 11.5H14" />
      <path d="M10 15h1.6" />
      <path d="M12.4 15H14" />
    </>
  ),
  agents: (
    <>
      <path d="M4 4.5h16v11H4z" />
      <path d="M9 19.5h6" />
      <path d="M12 15.5v4" />
      <path d="M7.8 10s1.7-2.6 4.2-2.6 4.2 2.6 4.2 2.6-1.7 2.6-4.2 2.6S7.8 10 7.8 10z" />
      <circle cx="12" cy="10" r="0.9" />
    </>
  ),
  webResearch: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M4.5 10.5h12" />
      <path d="M10.5 4.5c1.8 1.7 2.6 3.7 2.6 6s-.8 4.3-2.6 6" />
      <path d="M10.5 4.5c-1.8 1.7-2.6 3.7-2.6 6s.8 4.3 2.6 6" />
      <path d="m15 15 4.5 4.5" />
    </>
  ),
  /** The infrastructure bill: a coin beside rising bars. */
  infraCost: (
    <>
      <path d="M4 20h16" />
      <path d="M6.5 16.5v-4" />
      <path d="M10.5 16.5v-7" />
      <path d="M14.5 16.5V13" />
      <circle cx="17.5" cy="7" r="3" />
    </>
  ),
  /** Governed auto-approval: a shield with a check. */
  autoApproval: (
    <>
      <path d="M12 3.5 19 6.5v5.2c0 4.3-2.9 7.3-7 8.8-4.1-1.5-7-4.5-7-8.8V6.5z" />
      <path d="m8.8 12 2.2 2.2 4.4-4.4" />
    </>
  ),
  /** A recorded session: a play mark inside a screen. */
  sessions: (
    <>
      <path d="M4 5.5h16v13H4z" />
      <path d="m10 9.2 5 2.8-5 2.8z" />
    </>
  ),
  /** A sheet that failed: the document fold with an alert mark. */
  uploadFailures: (
    <>
      <path d="M7 3.5h8.5L19 7v13.5H7z" />
      <path d="M15.5 3.5V7H19" />
      <path d="M13 10v4" />
      <path d="M13 17.25h.01" />
    </>
  ),
  agentFindings: (
    <>
      <path d="M5 4.5h14v15H5z" />
      <path d="M5 9h14" />
      <path d="M9.5 4.5v15" />
      <path d="M12 12.5h4.5" />
      <path d="M12 16h3" />
    </>
  ),
  /* Como funciona: three stages joined by the flow line. */
  howItWorks: (
    <>
      <circle cx="5.5" cy="7" r="2.25" />
      <path d="M10 4.75h4.5v4.5H10z" />
      <circle cx="18.5" cy="17" r="2.25" />
      <path d="M7.75 7H10" />
      <path d="M14.5 7h1a2 2 0 0 1 2 2v5.75" />
      <path d="M5.5 9.25V15a2 2 0 0 0 2 2h8.75" />
    </>
  ),
  demos: (
    <>
      <path d="M4 5.5h16v11H4z" />
      <path d="m10.5 8.6 4 2.4-4 2.4z" />
      <path d="M8 19.5h8" />
    </>
  ),
  operations: (
    <>
      <path d="M4.5 5.5h15v13h-15z" />
      <path d="M9.5 5.5v13" />
    </>
  ),
  developers: (
    <>
      <path d="m8 8-4 4 4 4" />
      <path d="m16 8 4 4-4 4" />
      <path d="m14 4-4 16" />
    </>
  ),
  back: (
    <>
      <path d="M14 6 8 12l6 6" />
      <path d="M8 12h10" />
    </>
  ),
  key: (
    <>
      <circle cx="8.5" cy="12" r="3.2" />
      <path d="M11.4 12h8.1" />
      <path d="M16.4 12v2.4" />
      <path d="M19.5 12v2.4" />
    </>
  ),
  logs: (
    <>
      <path d="M6 5.5h12v13H6z" />
      <path d="M9 9h6" />
      <path d="M9 12.5h6" />
      <path d="M9 16h4" />
    </>
  ),
  book: (
    <>
      <path d="M6 4.5h11.5v15H6z" />
      <path d="M9.5 4.5v15" />
      <path d="M12.2 8.5h3.4" />
      <path d="M12.2 11.5h3.4" />
    </>
  ),
  lab: (
    <>
      <path d="M9 3h6" />
      <path d="M10 3v6l-5.5 9.5A1.7 1.7 0 0 0 6 21h12a1.7 1.7 0 0 0 1.5-2.5L14 9V3" />
      <path d="M7.5 16h9" />
    </>
  ),
  video: (
    <>
      <path d="M4 5h12v14H4z" />
      <path d="m16 10 4-2v8l-4-2z" />
    </>
  ),
  quiz: (
    <>
      <path d="M5 4h14v16H5z" />
      <path d="M8 8h1M11 8h5M8 12h1M11 12h5M8 16h1M11 16h3" />
    </>
  ),
  check: <path d="M5.5 12.2 10 16.7 18.5 8" />,
  chevron: <path d="m7 9.5 5 5 5-5" />,
  chevronUp: <path d="m7 14.5 5-5 5 5" />,
  chevronLeft: <path d="m14.5 7-5 5 5 5" />,
  chevronRight: <path d="m9.5 7 5 5-5 5" />,
  leave: (
    <>
      <path d="M14 5h5v14h-5" />
      <path d="M4.5 12h10" />
      <path d="m8 8.5-3.5 3.5L8 15.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.25" />
      <path d="m15.25 15.25 4.5 4.5" />
    </>
  ),
  close: <path d="M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5" />,
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m13.5 6.5 5.5 5.5-5.5 5.5" />
    </>
  ),
  sortAsc: (
    <>
      <path d="M12 19V5" />
      <path d="m6.5 10.5 5.5-5.5 5.5 5.5" />
    </>
  ),
  sortDesc: (
    <>
      <path d="M12 5v14" />
      <path d="m6.5 13.5 5.5 5.5 5.5-5.5" />
    </>
  ),
  sortNone: (
    <>
      <path d="m8 9.5 4-4 4 4" />
      <path d="m8 14.5 4 4 4-4" />
    </>
  ),
  filter: <path d="M4.5 6h15l-5.8 6.8v5.4l-3.4 1.6v-7z" />,
  columns: (
    <>
      <path d="M4.5 5.5h15v13h-15z" />
      <path d="M9.5 5.5v13" />
      <path d="M14.5 5.5v13" />
    </>
  ),
  density: (
    <>
      <path d="M4.5 6h15" />
      <path d="M4.5 10h15" />
      <path d="M4.5 14h15" />
      <path d="M4.5 18h15" />
    </>
  ),
  download: (
    <>
      <path d="M12 4.5v10" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M5 19.5h14" />
    </>
  ),
  external: (
    <>
      <path d="M13.5 4.5h6v6" />
      <path d="M19.5 4.5 11 13" />
      <path d="M17 13.5v6H4.5V7h6" />
    </>
  ),
  refresh: (
    <>
      <path d="M19 12a7 7 0 1 1-2.05-4.95" />
      <path d="M19.5 4.5v4h-4" />
    </>
  ),
  undo: (
    <>
      <path d="M9 14 4.5 9.5 9 5" />
      <path d="M4.5 9.5h10a5 5 0 0 1 0 10H11" />
    </>
  ),
  flag: (
    <>
      <path d="M6 20.5v-16" />
      <path d="M6 5h11l-2.5 4 2.5 4H6" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
  success: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m8.5 12.2 2.4 2.4 4.6-4.8" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4.8 20.2 19H3.8L12 4.8Z" />
      <path d="M12 10v4" />
      <path d="M12 16.6h.01" />
    </>
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" />
    </>
  ),
  calendar: (
    <>
      <rect x="4.5" y="6.5" width="15" height="13.5" rx="1.5" />
      <path d="M8 4.5v3.5M16 4.5v3.5M4.5 10.5h15" />
    </>
  ),
  lock: (
    <>
      <rect x="6" y="11" width="12" height="9" rx="1.5" />
      <path d="M8.5 11V8.2a3.5 3.5 0 0 1 7 0V11" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="2.4" />
      <path d="M4.8 17.2c.4-2.4 2.2-3.7 4.2-3.7s3.8 1.3 4.2 3.7" />
      <circle cx="15.4" cy="8.6" r="2" />
      <path d="M14.4 13.6c1.7.2 3.2 1.3 3.6 3.2" />
    </>
  ),
  disabled: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="m7.4 16.6 9.2-9.2" />
    </>
  ),
  unavailable: (
    <>
      <path d="M12 4.8 20.2 19H3.8L12 4.8Z" />
      <path d="M12 9.6v4.2" />
      <path d="M12 16.6v.2" />
    </>
  ),
};

export const ICON_NAMES = Object.keys(ICONS) as IconName[];

/**
 * One catalogue icon. Decorative by default (`aria-hidden`); pass `label`
 * only when the icon is the sole content of its control.
 */
export function Icon({
  name,
  size,
  className,
  label,
  ...props
}: {
  name: IconName;
  size?: IconSize;
  className?: string;
  label?: string;
} & Omit<SVGProps<SVGSVGElement>, "viewBox" | "children" | "name">) {
  return (
    <svg
      className={className ? `icon ${className}` : "icon"}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-size={size}
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
      focusable="false"
      {...props}
    >
      {ICONS[name]}
    </svg>
  );
}
